// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ** Components
import Section from '@/components/common/Section';
import AnchorNavigation from '@/components/common/AnchorNavigation';
import PageContentContainer from '@/components/common/PageContentContainer';

// ** Block Imports
import ComponentBlocksEvents from './ComponentBlocksEvents';
import ComponentBlocksTextBlock from './ComponentBlocksTextBlock';
import ComponentBlocksFileBlock from './ComponentBlocksFileBlock';
import ComponentBlocksTeamBlock from './ComponentBlocksTeamBlock';
import ComponentBlocksVideoBlock from './ComponentBlocksVideoBlock';
import ComponentBlocksWysiwygBlock from './ComponentBlocksWysiwygBlock';
import ComponentBlocksSectionBlock from './ComponentBlocksSectionBlock';
import ComponentBlocksCardsRow from './ComponentBlocksCardsRow';
import ComponentBlocksPodcastBlock from './ComponentBlocksPodcastBlock';
import ComponentBlocksGraphicBlock from './ComponentBlocksGraphicBlock';
import ComponentBlocksPartnerBlock from './ComponentBlocksPartnerBlock';
import ComponentBlocksBildTextBlock from './ComponentBlocksBildTextBlock';
import ComponentBlocksButtonLinkRow from './ComponentBlocksButtonLinkRow';
import ComponentBlocksCarouselBlock from './ComponentBlocksCarouselBlock';
import ComponentBlocksImageGridBlock from './ComponentBlocksImageGridBlock';
import ComponentBlocksAccordionGroup from './ComponentBlocksAccordionGroup';
import ComponentBlocksAccordionCards from './ComponentBlocksAccordionCards';
import ComponentBlocksGridNavigation from './ComponentBlocksGridNavigation';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type {
	ComponentElementsAnchorTab,
	PageSectionPageSectionContentDynamicZone,
	ComponentBlocksAnchor as BlockAnchorProps
} from '@/types/graphql';

type ComponentBlocksAnchorProps = StrapiComponentProps<BlockAnchorProps>;
type BlockWithTypename = PageSectionPageSectionContentDynamicZone & {
	__typename: string;
	id: string | number;
};

const componentsMap: Record<string, React.ComponentType<any>> = {
	ComponentBlocksEvents,
	ComponentBlocksTextBlock,
	ComponentBlocksFileBlock,
	ComponentBlocksTeamBlock,
	ComponentBlocksVideoBlock,
	ComponentBlocksWysiwygBlock,
	ComponentBlocksSectionBlock,
	ComponentBlocksCardsRow,
	ComponentBlocksPodcastBlock,
	ComponentBlocksGraphicBlock,
	ComponentBlocksPartnerBlock,
	ComponentBlocksBildTextBlock,
	ComponentBlocksButtonLinkRow,
	ComponentBlocksCarouselBlock,
	ComponentBlocksImageGridBlock,
	ComponentBlocksAccordionGroup,
	ComponentBlocksAccordionCards,
	ComponentBlocksGridNavigation
};

const COMPONENT_CONFIG: Record<string, any> = {
	ComponentBlocksCarouselBlock: {
		containerSx: { pt: { xs: 4, md: 20 }, pb: { xs: 10, md: 20 } }
	},
	ComponentBlocksEvents: {
		containerSx: { pt: { xs: 4, md: 20 }, pb: { xs: 4, md: 10 } }
	},
	ComponentBlocksButtonLinkRow: { containerSx: { py: { xs: 8, md: 20 } } },
	ComponentBlocksPartnerBlock: { containerSx: { py: { xs: 6, md: 10 } } },
	ComponentBlocksAccordionGroup: { containerSx: { py: 4 } },
	default: { containerSx: { py: 10 } }
};

/**
 * Sub-component to handle the rendering of dynamic zone content.
 * This solves the restProps dependency paradox.
 */
const SectionRenderer = ({ section, locale, ...restProps }: { section: any; locale: string; [key: string]: any }) => {
	if (!section || !Array.isArray(section.pageSectionContent)) return null;

	return (
		<>
			{section.pageSectionContent.map((block: any, index: number) => {
				const blockData = block as BlockWithTypename;
				const typeName = blockData?.__typename;
				const Component = componentsMap[typeName];
				if (!Component) return null;

				const config = COMPONENT_CONFIG[typeName] || COMPONENT_CONFIG.default;
				const isLast = index === section.pageSectionContent.length - 1;

				return (
					<Component
						key={`${typeName}-${blockData.id}`}
						{...block}
						{...restProps}
						locale={locale}
						containerSx={{
							...config.containerSx,
							...(isLast && { pb: 10 })
						}}
					/>
				);
			})}
		</>
	);
};

const ComponentBlockAnchor = ({
	containerSx,
	anchorTabs,
	anchorSectionTitle,
	anchorShowBorder,
	anchorType,
	locale,
	...restProps
}: ComponentBlocksAnchorProps) => {
	const isNavigationMode = anchorType === 'Content_Navigation';
	const isManualScrolling = React.useRef(false);

	// 1. Generate Nav Items
	const navItems = React.useMemo(() => {
		return (anchorTabs ?? [])
			.filter((tab): tab is ComponentElementsAnchorTab => !!tab)
			.map((tab) => ({
				label: tab.anchorTabTitle || '',
				id: tab.id,
				href: `#${tab.anchorTabContent?.pageSectionIdentifier || tab.id}`
			}));
	}, [anchorTabs]);

	const [activeId, setActiveId] = React.useState(() => navItems[0]?.id || '');

	// 2. Find Active Tab Data (for Tabbed mode)
	const activeTab = React.useMemo(() => {
		return anchorTabs?.find((tab) => tab?.id === activeId);
	}, [anchorTabs, activeId]);

	// 3. Scroll-Spy Logic (Intersection Observer)
	React.useEffect(() => {
		if (!isNavigationMode || navItems.length === 0) return;

		const handleIntersect = (entries: IntersectionObserverEntry[]) => {
			// Guard: If scrolling was triggered by a click, don't update activeId
			if (isManualScrolling.current) return;

			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const id = entry.target.getAttribute('data-tabid');
					if (id) setActiveId(id);
				}
			});
		};

		const observer = new IntersectionObserver(handleIntersect, {
			rootMargin: '-120px 0px -50% 0px', // Focus the active zone near the top
			threshold: 0
		});

		navItems.forEach((item) => {
			const targetTab = anchorTabs?.find((t) => t?.id === item.id);
			const domId = targetTab?.anchorTabContent?.pageSectionIdentifier || item.id;
			const el = document.getElementById(domId);
			if (el) observer.observe(el);
		});

		return () => observer.disconnect();
	}, [isNavigationMode, navItems, anchorTabs]);

	// 4. Click Handler
	const handleTabClick = (id: string) => {
		setActiveId(id);

		if (isNavigationMode) {
			const targetTab = anchorTabs?.find((t) => t?.id === id);
			const domId = targetTab?.anchorTabContent?.pageSectionIdentifier || id;
			const el = document.getElementById(domId);

			if (el) {
				isManualScrolling.current = true;
				window.dispatchEvent(new CustomEvent('programmatic-scroll', { detail: true }));
				el.scrollIntoView({ behavior: 'smooth', block: 'start' });

				setTimeout(() => {
					isManualScrolling.current = false;
					window.dispatchEvent(new CustomEvent('programmatic-scroll', { detail: false }));
				}, 1000);
			}
		}
	};

	return (
		<Section containerSx={containerSx} fullWidth>
			{anchorSectionTitle && (
				<PageContentContainer>
					<Typography
						variant="title-lg"
						component="h2"
						sx={{
							mb: 6,
							fontSize: { xs: '2rem', md: '2.5rem' },
							lineHeight: { xs: '2.5rem', md: '3rem' }
						}}
					>
						{anchorSectionTitle}
					</Typography>
				</PageContentContainer>
			)}

			<Stack spacing={0}>
				{/* Navigation Bar */}
				<Box
					sx={{
						position: isNavigationMode ? 'sticky' : 'relative',
						top: isNavigationMode ? 'var(--scroll-navbar-offset, 0px)' : 'auto',
						zIndex: 100,
						bgcolor: 'background.paper',
						borderBottom: anchorShowBorder ? 1 : 0,
						borderColor: 'divider',
						transition: 'top 220ms ease'
					}}
				>
					<AnchorNavigation items={navItems} activeId={activeId} showBorder={false} onItemClick={handleTabClick} />
				</Box>

				{/* Content Area */}
				<Box sx={{ mt: 4 }}>
					{isNavigationMode
						? anchorTabs?.map((tab) => (
								<Box
									key={tab?.id}
									id={tab?.anchorTabContent?.pageSectionIdentifier || tab?.id}
									data-tabid={tab?.id}
									sx={{
										scrollMarginTop: { xs: '80px', md: '140px' }
									}}
								>
									<SectionRenderer section={tab?.anchorTabContent} locale={locale} {...restProps} />
								</Box>
							))
						: activeTab?.anchorTabContent && (
								<Box id={activeTab.anchorTabContent.pageSectionIdentifier || undefined}>
									<SectionRenderer section={activeTab.anchorTabContent} locale={locale} {...restProps} />
								</Box>
							)}
				</Box>
			</Stack>
		</Section>
	);
};

export default ComponentBlockAnchor;
